import React from "react";
import { theme } from "../../theme";

const typeIcons = {
  evidence: "📎",
  report: "📊",
  document: "📄",
  crm: "💼",
  billing: "💳",
  system: "⚙️"
};

function formatRelativeTime(value) {
  const date = new Date(value);
  const time = date.getTime();

  if (!Number.isFinite(time)) return "Recently";

  const seconds = Math.max(0, Math.floor((Date.now() - time) / 1000));

  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

  return date.toLocaleDateString();
}

export default function NotificationItem({
  notification,
  onOpen,
  onMarkRead,
  onRemove
}) {
  const isUnread = !notification.read;
  const isHighPriority = notification.priority === "high";

  return (
    <article
      style={{
        ...styles.item,
        ...(isUnread ? styles.unread : {})
      }}
    >
      <button
        type="button"
        onClick={() => onOpen(notification)}
        style={styles.contentButton}
      >
        <span style={styles.icon} aria-hidden="true">
          {typeIcons[notification.type] || typeIcons.system}
        </span>

        <span style={styles.content}>
          <span style={styles.titleRow}>
            <strong style={styles.title}>{notification.title}</strong>
            {isHighPriority && <span style={styles.priority}>High</span>}
          </span>

          <span style={styles.message}>{notification.message}</span>
          <span style={styles.time}>{formatRelativeTime(notification.createdAt)}</span>
        </span>
      </button>

      <div style={styles.actions}>
        {isUnread && (
          <button
            type="button"
            onClick={() => onMarkRead(notification.id)}
            style={styles.actionButton}
          >
            Mark read
          </button>
        )}

        <button
          type="button"
          onClick={() => onRemove(notification.id)}
          style={{ ...styles.actionButton, color: theme.colors.danger }}
          aria-label={`Remove ${notification.title}`}
        >
          Remove
        </button>
      </div>
    </article>
  );
}

const styles = {
  item: {
    borderBottom: `1px solid ${theme.colors.border}`,
    background: theme.colors.card
  },
  unread: {
    background: "#ecfdf5",
    boxShadow: "inset 3px 0 0 #0f766e"
  },
  contentButton: {
    width: "100%",
    display: "flex",
    gap: "12px",
    padding: "15px 16px 10px",
    border: "none",
    background: "transparent",
    textAlign: "left",
    cursor: "pointer"
  },
  icon: {
    width: "34px",
    height: "34px",
    flex: "0 0 auto",
    display: "grid",
    placeItems: "center",
    borderRadius: "10px",
    background: "#f1f5f9",
    fontSize: "17px"
  },
  content: {
    minWidth: 0,
    display: "grid",
    gap: "5px"
  },
  titleRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    justifyContent: "space-between"
  },
  title: {
    color: theme.colors.text,
    fontSize: "13px",
    lineHeight: 1.35
  },
  priority: {
    padding: "2px 7px",
    borderRadius: theme.radius.pill,
    background: "#fee2e2",
    color: "#991b1b",
    fontSize: "10px",
    fontWeight: 900,
    textTransform: "uppercase"
  },
  message: {
    color: theme.colors.muted,
    fontSize: "12px",
    lineHeight: 1.5
  },
  time: {
    color: "#94a3b8",
    fontSize: "11px"
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "0 16px 12px"
  },
  actionButton: {
    border: "none",
    background: "transparent",
    color: theme.colors.primary,
    fontSize: "11px",
    fontWeight: 800,
    cursor: "pointer",
    padding: "2px"
  }
};
